import { AxiosError } from 'axios';
import Auth, { AUTH_HEADER_KEYS } from '../api/auth';
import { clearCookiesOnLogout } from '../store/utils/api';

// 401 from these is a normal outcome (wrong password / already signed out),
// never a sign of an expired session.
const AUTH_URLS = ['auth/sign_in', 'auth/sign_out'];

const authHeadersFromCookie = () => {
  const authData = Auth.getAuthData();
  if (!authData || !authData['access-token']) return null;
  const headers = {};
  AUTH_HEADER_KEYS.forEach(key => {
    headers[key] = authData[key];
  });
  return headers;
};

let loggingOut = false;
const logoutOnce = () => {
  if (loggingOut) return;
  loggingOut = true;
  clearCookiesOnLogout();
};

// Deduplicate concurrent validity checks: with polling, a burst of parallel
// requests can all hit 401 at once.
let validityCheckPromise = null;

// A 401 mid-session has two very different causes and only one warrants a
// logout:
//  1. The tab holds a stale in-memory token while the cookie has a fresh one
//     (another tab/login refreshed the session) -> self-heal: retry with the
//     cookie token, never log out.
//  2. The token was evicted or expired server-side -> confirm via
//     auth/validate_token, and only log out when that also returns 401.
export const createAuthErrorHandler = wootApi => async error => {
  const { response, config } = error;
  if (response?.status !== 401) throw error;

  const url = config?.url || '';
  if (AUTH_URLS.some(authUrl => url.includes(authUrl))) throw error;

  // The validity check itself was rejected with the freshest credentials we
  // have -> the session is genuinely gone.
  if (url.includes('auth/validate_token')) {
    logoutOnce();
    throw error;
  }

  // Self-heal: the cookie may hold a newer token than the one this request
  // was sent with.
  const freshHeaders = authHeadersFromCookie();
  const usedToken = config?.headers?.['access-token'];
  if (freshHeaders && freshHeaders['access-token'] !== usedToken) {
    Object.assign(wootApi.defaults.headers.common, freshHeaders);
    if (!config.authRetryDone) {
      return wootApi({
        ...config,
        authRetryDone: true,
        headers: { ...config.headers, ...freshHeaders },
      });
    }
    throw error;
  }

  // Same token in cookie and request: confirm the session is dead before
  // logging the user out. A success here means the 401 was transient
  // (e.g. a server hiccup) and the user keeps working.
  if (!validityCheckPromise) {
    validityCheckPromise = wootApi
      .get('auth/validate_token')
      .catch(() => {
        // 401 is handled above via logoutOnce; other errors (network, 5xx)
        // must not log the user out.
      })
      .finally(() => {
        validityCheckPromise = null;
      });
  }
  await validityCheckPromise;
  throw error;
};

// axios resolves an XHR that ended with status 0 (no response, e.g. dropped
// by the browser) as a success with `data: ''`, so callers that read the body
// crash instead of hitting their error handling. Reject it like a network error.
export const rejectEmptyResponse = response => {
  if (response.status) return response;
  throw new AxiosError(
    'Network Error',
    AxiosError.ERR_NETWORK,
    response.config,
    response.request
  );
};

export default axios => {
  const { apiHost = '' } = window.chatwootConfig || {};
  const wootApi = axios.create({ baseURL: `${apiHost}/` });
  // Add Auth Headers to requests if logged in
  Object.assign(wootApi.defaults.headers.common, authHeadersFromCookie() || {});
  wootApi.interceptors.response.use(
    rejectEmptyResponse,
    createAuthErrorHandler(wootApi)
  );
  return wootApi;
};
